import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Vertrag, VertragAkte as VertragAkteDaten, VertragPosition, VertragRechnungKurz, VertragVersion,
} from '../types';
import { useRechte } from '../hooks/useRechte';
import { darfBelegeSenden, hatRecht } from '../lib/rechte';
import {
  betrag, darfKulanz, euro, fruehestesKuendigungsdatum, intervallText, periodenSumme, positionsDiff, PositionsFeld,
  stornierteLetzteRechnung, versionLabel,
} from '../lib/vertrag';
import { FESTSCHREIBEN_FRAGE, fehlerText } from '../lib/belegKarte';
import { benutzerRollen } from '../lib/tabRollen';
import {
  kannZuAuftragNavigieren, kannZuPartnerNavigieren, navigiereZuAuftrag, navigiereZuPartner,
} from '../lib/navigation';
import { leseRolleAusSpeicher } from './RegieberichtForm';
import AktivitaetenBlock from './AktivitaetenBlock';
import VertragVersionDialog from './VertragVersionDialog';
import VertragKuendigenDialog from './VertragKuendigenDialog';
import './AuftragsAkte.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// ── Darstellung ────────────────────────────────────────────────────────────

function datum(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE');
}

const STATUS_CHIP: Record<string, React.CSSProperties> = {
  ENTWURF:   { background: 'var(--bone-2)', color: 'var(--ink-2)', border: '1px solid var(--bone-3)' },
  AKTIV:     { background: 'var(--bone-2)', color: 'var(--green-500)', border: '1px solid var(--green-400)' },
  GEKUENDIGT:{ background: 'var(--bone-2)', color: 'var(--red)', border: '1px solid var(--red)' },
  ABGELAUFEN:{ background: 'var(--bone-2)', color: 'var(--gold)', border: '1px solid var(--bone-2)' },
};

export const chipBase: React.CSSProperties = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 0, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
};

/** Status-Chip eines Vertrags (auch in VertragList verwendet). */
export const VertragStatusChip: React.FC<{ status: string }> = ({ status }) => (
  <span style={{ ...chipBase, ...(STATUS_CHIP[status] ?? {}) }}>{status}</span>
);

/** Vertragsstatus → Farbklasse der Auftragsakte (Karte „Vertrag“). */
export function vertragStatusKind(status: string): 'offen' | 'fortschritt' | 'abschluss' | 'storniert' {
  if (status === 'AKTIV') return 'abschluss';
  if (status === 'GEKUENDIGT') return 'storniert';
  if (status === 'ABGELAUFEN') return 'fortschritt';
  return 'offen';
}

/** Rechnungsstatus → Farbklasse der Auftragsakte. */
export function rechnungStatusKind(status: string): 'offen' | 'fortschritt' | 'abschluss' | 'storniert' {
  if (status === 'STORNIERT') return 'storniert';
  if (status === 'ENTWURF') return 'offen';
  if (status === 'BEZAHLT') return 'abschluss';
  return 'fortschritt';
}

const FELD_TEXT: Record<PositionsFeld, string> = {
  menge: 'Menge', preis_einzeln: 'Einzelpreis', rabatt_prozent: 'Rabatt', beschreibung: 'Beschreibung',
};

function feldWert(feld: PositionsFeld, v: string | number | null): string {
  if (v == null || v === '') return '—';
  if (feld === 'preis_einzeln') return euro(v);
  if (feld === 'rabatt_prozent') return `${betrag(v).toLocaleString('de-DE')} %`;
  if (feld === 'menge') return betrag(v).toLocaleString('de-DE');
  return String(v);
}

const posName = (p: VertragPosition) => p.beschreibung || `Artikel #${p.artikel_id ?? '?'}`;

// ── Teilkarten ─────────────────────────────────────────────────────────────

const PositionenTabelle: React.FC<{ positionen: VertragPosition[] }> = ({ positionen }) => (
  <table className="aa-positionen-table">
    <thead>
      <tr><th>Pos.</th><th>Beschreibung</th><th>Menge</th><th>Einzelpreis</th><th>Rabatt</th><th>Summe</th></tr>
    </thead>
    <tbody>
      {positionen.map(p => (
        <tr key={p.vertrag_position_id}>
          <td>{p.positions_nr}</td>
          <td>{posName(p)}</td>
          <td>{betrag(p.menge).toLocaleString('de-DE')} {p.einheit ?? ''}</td>
          <td>{euro(p.preis_einzeln)}</td>
          <td>{p.rabatt_prozent != null ? `${betrag(p.rabatt_prozent).toLocaleString('de-DE')} %` : '—'}</td>
          <td>{euro(p.preis_gesamt)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

/** Änderungen gegenüber der Vorversion (neu / entfernt / geändert), farbig hervorgehoben. */
const VersionDiff: React.FC<{ alt: VertragVersion; neu: VertragVersion }> = ({ alt, neu }) => {
  const d = positionsDiff(alt.positionen, neu.positionen);
  const name = (artikelId: number) => {
    const p = neu.positionen.find(x => x.artikel_id === artikelId);
    return p ? posName(p) : `Artikel #${artikelId}`;
  };
  if (d.neu.length + d.entfernt.length + d.geaendert.length === 0) {
    return <p className="aa-empty">Positionen unverändert gegenüber {alt.label}.</p>;
  }
  return (
    <ul style={{ margin: '4px 0 8px', paddingLeft: 18, fontSize: 13 }}>
      {d.neu.map(p => (
        <li key={`n${p.vertrag_position_id}`} style={{ color: 'var(--green-500)' }}>
          + {posName(p)}: {betrag(p.menge).toLocaleString('de-DE')} × {euro(p.preis_einzeln)}
        </li>
      ))}
      {d.entfernt.map(p => (
        <li key={`e${p.vertrag_position_id}`} style={{ color: 'var(--red)' }}>− {posName(p)} entfernt</li>
      ))}
      {d.geaendert.map((g, i) => (
        <li key={`g${i}`} style={{ color: 'var(--gold)' }}>
          {name(g.artikel_id)}: {FELD_TEXT[g.feld]} {feldWert(g.feld, g.von)} → {feldWert(g.feld, g.nach)}
        </li>
      ))}
    </ul>
  );
};

// ── Akte ───────────────────────────────────────────────────────────────────

interface VertragAkteProps {
  vertragId: number;
  onClose: () => void;
}

type Dialog = 'version' | 'kuendigen' | 'erneut' | null;

/** Vertragsakte (Spec §6.2): Kopf, aktuelle Version, Versionen mit Diff, Rechnungen,
 *  Ursprungsauftrag, Objektakte (AktivitaetenBlock mit Belegen). */
const VertragAkte: React.FC<VertragAkteProps> = ({ vertragId, onClose }) => {
  const [akte, setAkte] = useState<VertragAkteDaten | null>(null);
  const [partnerName, setPartnerName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [offeneVersion, setOffeneVersion] = useState<number | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [aktionFehler, setAktionFehler] = useState<string | null>(null);
  const [aktionInfo, setAktionInfo] = useState<string | null>(null);
  const [erneutVon, setErneutVon] = useState('');

  const { rechte, rollen, geladen } = useRechte();
  const alleRollen = benutzerRollen(rollen, geladen, leseRolleAusSpeicher());

  const ladeAkte = useCallback(async () => {
    setError(null);
    try {
      const res = await axios.get<VertragAkteDaten>(`${API_URL}/api/vertraege/${vertragId}/akte`);
      setAkte(res.data);
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setError(fehlerText(detail, 'Vertragsakte konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, [vertragId]);

  useEffect(() => { setLoading(true); ladeAkte(); }, [ladeAkte]);

  // Partnername für den Kopf (VertragOut trägt nur die ID); Fehler sind unkritisch.
  const partnerId = akte?.vertrag.partner_id ?? null;
  useEffect(() => {
    if (partnerId == null) { setPartnerName(null); return; }
    axios.get<{ name?: string }>(`${API_URL}/api/partners/${partnerId}`)
      .then(r => setPartnerName(r.data.name ?? null))
      .catch(() => setPartnerName(null));
  }, [partnerId]);

  /** Aktion ausführen: Fehler/Info anzeigen, danach Akte neu laden. `alsInfo` erkennt
   *  erwartbare Antworten (z. B. 409 „Keine fällige Periode“), die kein Fehler sind. */
  const fuehreAus = async (
    aufruf: () => Promise<string>, fallback: string, alsInfo?: (status: number, detail: string) => boolean,
  ) => {
    setAktionFehler(null);
    setAktionInfo(null);
    setLaeuft(true);
    try {
      setAktionInfo(await aufruf());
      await ladeAkte();
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      const status = axios.isAxiosError(err) ? err.response?.status ?? 0 : 0;
      const text = fehlerText(detail, fallback);
      if (alsInfo?.(status, text)) setAktionInfo(text);
      else setAktionFehler(text);
    } finally {
      setLaeuft(false);
    }
  };

  const jetztAbrechnen = () => fuehreAus(async () => {
    // 201 liefert mindestens einen Entwurf; ohne fällige Periode antwortet die API mit 409.
    const res = await axios.post<VertragRechnungKurz[]>(`${API_URL}/api/vertraege/${vertragId}/abrechnen`);
    return res.data.length === 1 ? '1 Rechnungsentwurf angelegt.' : `${res.data.length} Rechnungsentwürfe angelegt.`;
  }, 'Abrechnung fehlgeschlagen.', (status, text) => status === 409 && text.includes('Keine fällige Periode'));

  const freigeben = (rechnungId: number) => {
    if (!window.confirm(FESTSCHREIBEN_FRAGE)) return;
    fuehreAus(async () => {
      await axios.post(`${API_URL}/api/dokumente/ausgangsrechnungen/${rechnungId}/festschreiben`);
      return 'Rechnung freigegeben.';
    }, 'Rechnung konnte nicht freigegeben werden.');
  };

  const periodeErneut = () => fuehreAus(async () => {
    await axios.post(`${API_URL}/api/vertraege/${vertragId}/periode-erneut`, { von: erneutVon });
    setDialog(null);
    return 'Periode erneut abgerechnet (neuer Entwurf).';
  }, 'Periode konnte nicht erneut abgerechnet werden.');

  if (loading) {
    return <div className="aa-container"><div className="aa-loading">Vertragsakte wird geladen…</div></div>;
  }
  if (!akte) {
    return (
      <div className="aa-container">
        <button className="aa-back-btn" onClick={onClose}>← Zurück</button>
        <div className="aa-error">{error || 'Vertrag nicht gefunden.'}</div>
      </div>
    );
  }

  const v: Vertrag = akte.vertrag;
  const aktuell = akte.aktuelle_version;
  const ursprung = akte.auftrag;
  const storno = stornierteLetzteRechnung(akte.rechnungen);
  const laufend = v.status !== 'ABGELAUFEN';
  const darfVersion = hatRecht(rechte, 'vertrag.aendern') && v.intervall != null && laufend;
  const darfKuendigen = hatRecht(rechte, 'vertrag.loeschen') && (v.status === 'AKTIV' || v.status === 'ENTWURF');
  const darfAbrechnen = hatRecht(rechte, 'vertrag.anlegen') && v.intervall != null && laufend;
  const darfFreigeben = darfBelegeSenden(rechte);
  const toggleDialog = (d: Dialog) => { setAktionFehler(null); setDialog(o => (o === d ? null : d)); };

  return (
    <div className="aa-container">
      {error && <div className="aa-inline-fehler">{error}</div>}
      {/* 1. Kopf */}
      <div className="aa-card">
        <button className="aa-back-btn" onClick={onClose}>← Zurück</button>
        <h2 className="aa-auftrag-nr" style={{ marginBottom: 4 }}>
          {v.vertrag_nr || `Vertrag #${v.vertrag_id}`} <VertragStatusChip status={v.status} />
        </h2>
        <div style={{ fontWeight: 500 }}>{v.titel}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginTop: 12, fontSize: 13 }}>
          <div>
            Partner:{' '}
            {v.partner_id != null && kannZuPartnerNavigieren() ? (
              <button type="button" className="k-btn k-btn--ghost k-btn--sm" onClick={() => navigiereZuPartner(v.partner_id as number)}>
                {partnerName || `Partner #${v.partner_id}`}
              </button>
            ) : (partnerName || (v.partner_id != null ? `#${v.partner_id}` : '—'))}
          </div>
          <div>Abrechnung: {v.intervall ? intervallText(v.intervall) : '—'}</div>
          <div>Laufzeit: {datum(v.laufzeit_start)} – {v.laufzeit_ende ? datum(v.laufzeit_ende) : 'unbefristet'}</div>
          <div>Kündigungsfrist: {v.kuendigungsfrist_tage} Tage{v.auto_verlaengerung ? ', verlängert sich automatisch' : ''}</div>
          <div>Nächster Stichtag: <strong>{datum(v.naechste_abrechnung_am)}</strong></div>
          <div>
            Monatlich: <strong>{euro(v.monatlicher_betrag)}</strong>
            {aktuell && <span style={{ color: 'var(--ink-2)' }}> (gültig ab {datum(aktuell.gueltig_ab)})</span>}
          </div>
          {v.kuendigung_zum && (
            <div style={{ color: 'var(--red)' }}>
              Gekündigt zum {datum(v.kuendigung_zum)} (am {datum(v.gekuendigt_am)})
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {darfVersion && (
            <button className="k-btn k-btn--primary k-btn--sm" onClick={() => toggleDialog('version')} disabled={laeuft}>
              Ändern (neue Version)
            </button>
          )}
          {darfAbrechnen && (
            <button className="k-btn k-btn--ghost k-btn--sm" onClick={jetztAbrechnen} disabled={laeuft}>
              {laeuft ? 'Läuft…' : 'Jetzt abrechnen'}
            </button>
          )}
          {darfAbrechnen && storno && (
            <button className="k-btn k-btn--ghost k-btn--sm" disabled={laeuft}
                    onClick={() => { setErneutVon(storno.leistungszeitraum_von ?? ''); toggleDialog('erneut'); }}>
              Periode erneut abrechnen
            </button>
          )}
          {darfKuendigen && (
            <button className="k-btn k-btn--danger k-btn--sm" onClick={() => toggleDialog('kuendigen')} disabled={laeuft}>
              Kündigen
            </button>
          )}
        </div>
        {dialog === 'version' && (
          <VertragVersionDialog
            vertragId={v.vertrag_id}
            gueltigAbVorschlag={v.naechste_abrechnung_am}
            aktuelleVersion={aktuell}
            onGespeichert={() => { setDialog(null); setAktionInfo('Neue Version angelegt.'); ladeAkte(); }}
            onAbbrechen={() => setDialog(null)}
          />
        )}
        {dialog === 'kuendigen' && (
          <VertragKuendigenDialog
            vertragId={v.vertrag_id}
            datumVorschlag={fruehestesKuendigungsdatum(v)}
            kulanzErlaubt={darfKulanz(alleRollen)}
            onGekuendigt={() => { setDialog(null); setAktionInfo('Vertrag gekündigt.'); ladeAkte(); }}
            onAbbrechen={() => setDialog(null)}
          />
        )}
        {dialog === 'erneut' && storno && (
          <div className="aa-rechnung-dialog">
            <div className="aa-rechnung-dialog__zeile">
              <span>Stornierte Rechnung {storno.nummer_anzeige} — Periode ab</span>
              <input type="date" value={erneutVon} onChange={e => setErneutVon(e.target.value)} />
              <button className="aa-card-action" onClick={periodeErneut} disabled={laeuft || !erneutVon}>
                Neu abrechnen
              </button>
            </div>
          </div>
        )}
        {aktionFehler && <div className="aa-inline-fehler">{aktionFehler}</div>}
        {aktionInfo && <div style={{ color: 'var(--green-500)', fontSize: 13, marginTop: 8 }}>{aktionInfo}</div>}
      </div>

      <div className="aa-grid">
        {/* 2. Aktuelle Version */}
        <div className="aa-card">
          <h3 className="aa-card-title">
            Aktuelle Version {aktuell ? aktuell.label : versionLabel(v.aktuelle_version_nr)}
          </h3>
          {aktuell ? (
            <>
              <PositionenTabelle positionen={aktuell.positionen} />
              <p style={{ textAlign: 'right', fontWeight: 600, marginTop: 8 }}>
                Summe je Periode: {euro(periodenSumme(aktuell.positionen))}
              </p>
            </>
          ) : (
            <p className="aa-empty">Keine Versionen (Altvertrag ohne Positionen).</p>
          )}
        </div>

        {/* 3. Versionen */}
        <div className="aa-card">
          <h3 className="aa-card-title">Versionen</h3>
          {akte.versionen.length === 0 ? <p className="aa-empty">Keine Versionen</p> : (
            <ul className="aa-liste">
              {akte.versionen.map((ver, i) => {
                const offen = offeneVersion === ver.version_nr;
                return (
                  <li key={ver.vertrag_version_id} style={{ display: 'block', padding: '6px 0', borderBottom: '1px solid var(--bone-3)' }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="aa-liste-nr">{ver.label}</span>
                      <span>ab {datum(ver.gueltig_ab)}</span>
                      <span style={{ color: 'var(--ink-2)' }}>{ver.grund || '—'}</span>
                      <span style={{ marginLeft: 'auto' }}>{euro(periodenSumme(ver.positionen))} / Periode</span>
                      <button className="aa-mini-btn" onClick={() => setOffeneVersion(offen ? null : ver.version_nr)}>
                        {offen ? 'Zuklappen' : 'Positionen'}
                      </button>
                    </div>
                    {i > 0 && <VersionDiff alt={akte.versionen[i - 1]} neu={ver} />}
                    {offen && <PositionenTabelle positionen={ver.positionen} />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* 4. Rechnungen */}
        <div className="aa-card">
          <h3 className="aa-card-title">Rechnungen</h3>
          {akte.rechnungen.length === 0 ? <p className="aa-empty">Keine Rechnungen</p> : (
            <ul className="aa-liste aa-liste--rechnungen">
              {akte.rechnungen.map(r => (
                <li key={r.ausgangsrechnung_id} className="aa-liste-item">
                  <span className="aa-liste-nr">{r.nummer_anzeige}</span>
                  <span>{datum(r.leistungszeitraum_von)} – {datum(r.leistungszeitraum_bis)}</span>
                  <span>Version {r.version_label ?? '—'}</span>
                  <span className={`aa-status-badge aa-status-badge--${rechnungStatusKind(r.status)}`}>{r.status}</span>
                  <span className="aa-liste-datum">{euro(r.brutto_gesamt)}</span>
                  {r.status === 'ENTWURF' && darfFreigeben && (
                    <button className="aa-mini-btn" onClick={() => freigeben(r.ausgangsrechnung_id)} disabled={laeuft}>
                      Freigeben
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 5. Ursprungsauftrag */}
        <div className="aa-card">
          <h3 className="aa-card-title">Ursprungsauftrag</h3>
          {ursprung ? (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span className="aa-liste-nr">{ursprung.auftrag_nr}</span>
              <span className="aa-status-badge aa-status-badge--fortschritt">{ursprung.status}</span>
              {kannZuAuftragNavigieren() && (
                <button className="aa-mini-btn" onClick={() => navigiereZuAuftrag(ursprung.auftrag_id)}>
                  Auftragsakte öffnen
                </button>
              )}
            </div>
          ) : (
            <p className="aa-empty">Kein Ursprungsauftrag (Vertrag ohne Auftrag angelegt).</p>
          )}
        </div>
      </div>

      {/* 6. Objektakte */}
      <div className="aa-card">
        <AktivitaetenBlock entitaetTyp="vertrag" entitaetId={v.vertrag_id} mitBelegen />
      </div>
    </div>
  );
};

export default VertragAkte;
